import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import LoadingSpinner from "../components/LoadingSpinner";
import MinMaxManager from "../components/MinMaxManager";
import StateBadge from "../components/StateBadge";
import { useAuth } from "../contexts/AuthContext";
import { useScopedBoxes } from "../hooks/useScopedBoxes";
import { fmtWorkCenter } from "../lib/format";
import {
  type InventoryLocationRow,
  searchInventoryLocations,
} from "../services/inventoryApi";

/**
 * Inventory landing. A searchable smartbox picker (type to filter, click to
 * open that box's inventory) replaces the old full list. Platform + vendor
 * admins also get the Min/Max management tools (export/import/copy) folded in
 * below; customers/vendors-non-admin just get the picker.
 */
export const InventoryListPage: React.FC = () => {
  const { snapshot, loading, error } = useScopedBoxes();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  // Cross-fleet part search. Separate field from the smartbox-picker above
  // so the two intents don't fight each other: type a smartbox code here
  // and the smartbox picker handles it; type a SKU/part name in the
  // "Find a part" box and we switch to fleet-locator mode.
  const [partQuery, setPartQuery] = useState("");
  const [partResults, setPartResults] = useState<InventoryLocationRow[] | null>(null);
  const [partLoading, setPartLoading] = useState(false);
  const [partError, setPartError] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);
  // Ignore stale/out-of-order responses so a slow earlier search can't
  // overwrite newer results (see ProductPicker for the pattern).
  const reqIdRef = useRef(0);

  // Debounce the part search so each keystroke doesn't fire a query.
  useEffect(() => {
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    const q = partQuery.trim();
    if (q.length < 2) {
      setPartResults(null);
      setPartError(null);
      setPartLoading(false);
      return;
    }
    if (!token) return;
    setPartLoading(true);
    const id = ++reqIdRef.current;
    debounceRef.current = window.setTimeout(() => {
      searchInventoryLocations(q, token)
        .then((r) => {
          if (id !== reqIdRef.current) return;
          setPartResults(r.locations);
          setPartError(null);
        })
        .catch((e) => { if (id === reqIdRef.current) setPartError(e instanceof Error ? e.message : String(e)); })
        .finally(() => { if (id === reqIdRef.current) setPartLoading(false); });
    }, 300);
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
  }, [partQuery, token]);

  // When the part search has results, group by item for the rendering so
  // each part gets one block listing the smartboxes that carry it.
  const partsGrouped = useMemo(() => {
    if (!partResults) return null;
    const byItem = new Map<number, {
      item_id: number; sku: string | null; name: string | null;
      locations: InventoryLocationRow[];
    }>();
    for (const r of partResults) {
      const g = byItem.get(r.item_id);
      if (g) {
        g.locations.push(r);
      } else {
        byItem.set(r.item_id, {
          item_id: r.item_id, sku: r.sku, name: r.name, locations: [r],
        });
      }
    }
    return Array.from(byItem.values());
  }, [partResults]);

  // UP boxes first, then the rest reversed (matches the prior ordering), then
  // filter by the typed query against smartbox code / SCU / work center.
  const boxes = useMemo(() => {
    const all = snapshot?.boxes ?? [];
    const ordered = [
      ...all.filter((b) => b.state === "UP"),
      ...all.filter((b) => b.state !== "UP").reverse(),
    ];
    const q = query.trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter((b) =>
      [b.smartbox_code, b.scu_name, b.work_center, b.customer]
        .some((v) => (v ?? "").toLowerCase().includes(q)),
    );
  }, [snapshot, query]);

  const go = (scu: string): void => {
    void navigate(`/inventory/${encodeURIComponent(scu)}`);
  };

  return (
    <div className="p-4 md:p-6">
      <header className="mb-4">
        <h1 className="text-xl font-bold">Inventory</h1>
        <p className="text-xs text-secondary-500 mt-1">
          Search for a smartbox to see what's inside and what needs refilling.
        </p>
      </header>

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
          {error.message}
        </div>
      )}

      {loading && !snapshot ? (
        <LoadingSpinner label="loading smartboxes" />
      ) : (
        <div className="relative max-w-md">
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            // Delay close so a click on a result registers first.
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder="Search smartbox, SCU, or work center…"
            className="w-full border border-secondary-300 rounded-md px-3 py-2 text-sm"
          />
          {open && (
            <ul className="absolute z-20 mt-1 w-full max-h-80 overflow-auto bg-white border border-secondary-200 rounded-md shadow-lg divide-y divide-secondary-100">
              {boxes.length === 0 ? (
                <li className="px-3 py-3 text-sm text-secondary-400">No matching smartboxes.</li>
              ) : (
                boxes.map((b) => (
                  <li key={b.scu_name}>
                    <button
                      type="button"
                      // onMouseDown fires before input blur, so navigation isn't cancelled.
                      onMouseDown={() => go(b.scu_name)}
                      className="w-full text-left flex items-center justify-between px-3 py-2 hover:bg-secondary-50 min-h-[44px]"
                    >
                      <div className="min-w-0">
                        <div className="font-semibold text-sm">
                          {b.smartbox_code ?? b.scu_name}
                          {b.work_center && (
                            <span className="ml-1.5 font-normal text-secondary-600">
                              · {fmtWorkCenter(b.work_center)}
                            </span>
                          )}
                        </div>
                        {b.customer && (
                          <div className="text-xs text-secondary-500 truncate">{b.customer}</div>
                        )}
                      </div>
                      <StateBadge state={b.state} />
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      )}

      {/* Cross-fleet part search. Separate input from the smartbox picker so
          the two intents stay distinct. While the part query is active the
          results render BELOW (not as a dropdown) since each item can
          surface multiple smartboxes. */}
      <div className="mt-6 max-w-3xl">
        <label className="block text-sm font-semibold text-secondary-800 mb-1">
          Find a part across the fleet
        </label>
        <input
          type="search"
          value={partQuery}
          onChange={(e) => setPartQuery(e.target.value)}
          placeholder="Type a SKU or part name to see which smartboxes carry it…"
          className="w-full border border-secondary-300 rounded-md px-3 py-2 text-sm"
        />
        {partError && (
          <div className="mt-2 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
            {partError}
          </div>
        )}
        {partLoading && (
          <div className="mt-2 text-xs text-secondary-500">searching…</div>
        )}
        {partsGrouped && partsGrouped.length === 0 && !partLoading && (
          <div className="mt-3 text-sm text-secondary-500 bg-white border border-secondary-200 rounded p-4">
            No matching parts currently on hand anywhere you can see.
          </div>
        )}
        {partsGrouped && partsGrouped.length > 0 && (
          <div className="mt-3 space-y-3">
            {partsGrouped.map((g) => (
              <div
                key={g.item_id}
                className="bg-white border border-secondary-200 rounded-md overflow-hidden"
              >
                <div className="px-3 py-2 border-b border-secondary-200 bg-secondary-50">
                  <div className="font-mono font-semibold text-sm">
                    {g.sku ?? `#${g.item_id}`}
                  </div>
                  {g.name && (
                    <div className="text-xs text-secondary-600">{g.name}</div>
                  )}
                  <div className="text-[11px] text-secondary-500 mt-0.5">
                    on hand in {g.locations.length} smartbox
                    {g.locations.length === 1 ? "" : "es"} ·{" "}
                    {g.locations.reduce((n, l) => n + l.on_hand, 0)} total
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-secondary-600 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-3 py-1.5">Smartbox</th>
                      <th className="text-left px-3 py-1.5">Workcenter</th>
                      <th className="text-left px-3 py-1.5">Customer</th>
                      <th className="text-right px-3 py-1.5">On hand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.locations.map((l) => (
                      <tr
                        key={l.scu_name}
                        className="border-t border-secondary-100 hover:bg-secondary-50 cursor-pointer"
                        onClick={() => go(l.scu_name)}
                      >
                        <td className="px-3 py-1.5">
                          <span className="font-semibold">
                            {l.smartbox_code ?? l.scu_name}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-secondary-700">
                          {fmtWorkCenter(l.workcenter_name) || "—"}
                        </td>
                        <td className="px-3 py-1.5 text-secondary-700">
                          {l.customer_name ?? "—"}
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold">
                          {l.on_hand}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Min/Max management — renders only for platform + vendor admins. */}
      <MinMaxManager />
    </div>
  );
};

export default InventoryListPage;
